import { bulk, isArr, isStr } from 'lib/util'
import { get } from 'lib/sequence/component/state/value'
import { hidden, hide, show } from 'lib/sequence/component/state/hidden'

const bulkShow = bulk(show)
const bulkHide = bulk(hide)

/**
 * Returns whether the incoming data is valid.
 *
 * @param {string|string[]} containerKeys keys for the containers
 * @param {(string|Object)[]} fields      fields to check
 * @returns {boolean}
 */
const isInvalid = (containerKeys, fields) => (
  isArr(containerKeys)
    ? containerKeys?.length === 0
    : !containerKeys
)
  || fields.length === 0

/**
 * Decides whether to show/hide `containerKey` based on the value state
 * of `fields`.
 *
 * Each element of `fields` can either be a string, in which case the `value`
 * state of the component represented by the string is investigated, or an
 * object like `{ [string]: fn }`, in which case the callback is executed with
 * the component represented by the key of the object.
 *
 * In either case, if all fields have no value, the container will be hidden.
 *
 * @example
 * ```js
 * displayContainerWithFields(
 *   'additionalInformationContainer',
 *   [
 *     'foo', // check get(foo)
 *     { bar: c => get(c)?.length === 0 } // check if get(foo) is empty list
 *   ]
 * )(module),
 * ```
 *
 * The optional callback should return `true` if the field has no value / should
 * be hidden.
 *
 * This is useful for detail screens on mobile where we want to hide a section
 * of information (label + box below) only if all the section's fields are
 * without value.
 *
 * @param {?string|string[]} containerKeys
 * @param {(string | {[key: string]: (c: Gaia.Component.Spec) => boolean})[]} fields
 * @returns {function(): function(*, ...[*]): Promise<*>}
 */
export default (containerKeys = [], fields = []) => () => async (components, ...args) => {
  if (isInvalid(containerKeys, fields)) {
    console.error('displayContainerWithFields called with invalid container and/or fields', containerKeys, fields)
    return [components, ...args]
  }

  const containers = isStr(containerKeys)
    ? [components[containerKeys]]
    : containerKeys.map(c => components[c])

  const shouldHideContainer = fields
    .filter(p => !!p)
    .every((field) => {
      const isKey = isStr(field)

      const fieldName = isKey
        ? field
        : Object.keys(field)[0]
      const fieldComponent = components[fieldName]

      return isKey
        ? !get(fieldComponent) || hidden(fieldComponent)
        : field[fieldName](fieldComponent)
    })

  shouldHideContainer
    ? bulkHide(...containers)
    : bulkShow(...containers)

  return [components, ...args]
}
